import { createStore } from "vuex";
import { login, logout, getCurrentUser } from "@/services/auth";
import {
  getEmailAddresses,
  getEmailDetails,
  getMessages,
  getMessage,
  markMessageAsRead,
  moveMessageToFolder,
  deleteMessage,
  searchEmailAddresses,
  deleteEmailAddress,
  sendMessage,
  replyToEmail,
  validateEmailAddresses,
  getUserEmailAddresses,
  getEmailTemplates,
  getEmailSignatures,
  createEmailSignature,
  updateEmailSignature,
  deleteEmailSignature,
  getConversations,
  getConversation,
  markConversationAsRead,
  deleteConversation,
  bulkMessageAction,
  getMessageStats,
  searchConversations,
} from "@/services/api";

export default createStore({
  state: {
    user: null,
    token: localStorage.getItem("token") || null,
    isAuthenticated: !!localStorage.getItem("token"),
    messages: [],
    conversations: [],
    currentConversation: null,
    emailStats: null,
    userEmailAddresses: [],
    emailTemplates: [],
    emailSignatures: [],
    sendingEmails: new Set(),
  },
  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    username: (state) => state.user?.username || "",
    user: (state) => state.user,
    messages: (state) => state.messages,
    conversations: (state) => state.conversations,
    currentConversation: (state) => state.currentConversation,
    emailStats: (state) => state.emailStats,
    userEmailAddresses: (state) => state.userEmailAddresses,
    emailTemplates: (state) => state.emailTemplates,
    emailSignatures: (state) => state.emailSignatures,
    unreadMessageCount: (state) => state.emailStats?.unread || 0,
    isEmailSending: (state) => (messageId) =>
      state.sendingEmails.has(messageId),
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    SET_TOKEN(state, token) {
      state.token = token;
      state.isAuthenticated = !!token;
      if (token) {
        localStorage.setItem("token", token);
      } else {
        localStorage.removeItem("token");
      }
    },
    SET_MESSAGES(state, messages) {
      state.messages = messages;
    },
    ADD_MESSAGE(state, message) {
      state.messages.unshift(message);
    },
    UPDATE_MESSAGE(state, updatedMessage) {
      const index = state.messages.findIndex((m) => m.id === updatedMessage.id);
      if (index !== -1) {
        state.messages.splice(index, 1, updatedMessage);
      }
    },
    REMOVE_MESSAGE(state, messageId) {
      state.messages = state.messages.filter((m) => m.id !== messageId);
    },
    SET_CONVERSATIONS(state, conversations) {
      state.conversations = conversations;
    },
    SET_CURRENT_CONVERSATION(state, conversation) {
      state.currentConversation = conversation;
    },
    SET_EMAIL_STATS(state, stats) {
      state.emailStats = stats;
    },
    SET_USER_EMAIL_ADDRESSES(state, addresses) {
      state.userEmailAddresses = addresses;
    },
    SET_EMAIL_TEMPLATES(state, templates) {
      state.emailTemplates = templates;
    },
    SET_EMAIL_SIGNATURES(state, signatures) {
      state.emailSignatures = signatures;
    },
    ADD_EMAIL_SIGNATURE(state, signature) {
      state.emailSignatures.unshift(signature);
    },
    UPDATE_EMAIL_SIGNATURE(state, updatedSignature) {
      const index = state.emailSignatures.findIndex(
        (s) => s.id === updatedSignature.id
      );
      if (index !== -1) {
        state.emailSignatures.splice(index, 1, updatedSignature);
      }
    },
    REMOVE_EMAIL_SIGNATURE(state, signatureId) {
      state.emailSignatures = state.emailSignatures.filter(
        (s) => s.id !== signatureId
      );
    },
    SET_SENDING_EMAIL(state, { messageId, sending }) {
      if (sending) {
        state.sendingEmails.add(messageId);
      } else {
        state.sendingEmails.delete(messageId);
      }
    },
  },
  actions: {
    async initAuth({ commit, state }) {
      if (state.token && !state.user) {
        try {
          const user = await getCurrentUser();
          commit("SET_USER", user);
        } catch (error) {
          console.warn("Failed to initialize auth, clearing tokens:", error);
          commit("SET_USER", null);
          commit("SET_TOKEN", null);
          localStorage.removeItem("refresh");
        }
      }
    },

    async login({ commit }, credentials) {
      try {
        const response = await login(credentials);
        const { user, token, refresh } = response;
        commit("SET_USER", user);
        commit("SET_TOKEN", token);
        localStorage.setItem("refresh", refresh);

        // Return the full response with tokens for Login.vue
        return {
          user,
          access: token,
          refresh,
          token, // Backwards compatibility
        };
      } catch (error) {
        commit("SET_USER", null);
        commit("SET_TOKEN", null);
        localStorage.removeItem("refresh");
        throw error;
      }
    },

    async logout({ commit }) {
      try {
        await logout();
      } finally {
        commit("SET_USER", null);
        commit("SET_TOKEN", null);
        localStorage.removeItem("refresh");
      }
    },

    async fetchEmailAddresses(_, params = {}) {
      try {
        return await getEmailAddresses(params);
      } catch (error) {
        console.error("Error fetching email addresses:", error);
        throw error;
      }
    },

    async searchEmailAddresses(_, params) {
      try {
        return await searchEmailAddresses(params);
      } catch (error) {
        console.error("Error searching email addresses:", error);
        throw error;
      }
    },

    async fetchEmailDetails(_, emailId) {
      try {
        return await getEmailDetails(emailId);
      } catch (error) {
        console.error("Error fetching email details:", error);
        throw error;
      }
    },

    async fetchMessages({ commit }, { emailId, folder, page, page_size }) {
      try {
        const response = await getMessages(emailId, folder, {
          page,
          page_size,
        });
        if (page === 1) {
          commit("SET_MESSAGES", response.results || response);
        }
        return response;
      } catch (error) {
        console.error("Error fetching messages:", error);
        throw error;
      }
    },

    async fetchAllInboxMessages({ commit }, { page, page_size }) {
      try {
        const response = await getMessages(null, "inbox", {
          page,
          page_size,
        });
        if (page === 1) {
          commit("SET_MESSAGES", response.results || response);
        }
        return response;
      } catch (error) {
        console.error("Error fetching all messages:", error);
        throw error;
      }
    },

    async searchMessages(_, params) {
      try {
        return await getMessages(null, null, {
          search: params.query || params.search,
          page: params.page || 1,
          page_size: params.page_size || 50,
        });
      } catch (error) {
        console.error("Error searching messages:", error);
        throw error;
      }
    },

    async fetchMessage(_, messageId) {
      try {
        return await getMessage(messageId);
      } catch (error) {
        console.error("Error fetching message:", error);
        throw error;
      }
    },

    async markMessageAsRead({ commit }, messageId) {
      try {
        const response = await markMessageAsRead(messageId);
        commit("UPDATE_MESSAGE", { id: messageId, read: true });
        return response;
      } catch (error) {
        console.error("Error marking message as read:", error);
        throw error;
      }
    },

    async moveMessageToFolder({ commit }, { messageId, folder }) {
      try {
        const response = await moveMessageToFolder(messageId, folder);
        commit("UPDATE_MESSAGE", { id: messageId, folder });
        return response;
      } catch (error) {
        console.error("Error moving message to folder:", error);
        throw error;
      }
    },

    async deleteEmail(_, emailId) {
      try {
        return await deleteEmailAddress(emailId);
      } catch (error) {
        console.error("Error deleting email:", error);
        throw error;
      }
    },

    async deleteMessage({ commit }, messageId) {
      try {
        const response = await deleteMessage(messageId);
        commit("REMOVE_MESSAGE", messageId);
        return response;
      } catch (error) {
        console.error("Error deleting message:", error);
        throw error;
      }
    },

    async sendMessage({ commit }, messageId) {
      try {
        commit("SET_SENDING_EMAIL", { messageId, sending: true });
        const response = await sendMessage(messageId);
        if (response.data) {
          commit("UPDATE_MESSAGE", response.data);
        }
        return response;
      } catch (error) {
        console.error("Error sending message:", error);
        throw error;
      } finally {
        commit("SET_SENDING_EMAIL", { messageId, sending: false });
      }
    },

    async replyToEmail({ commit }, { messageId, replyData }) {
      try {
        const response = await replyToEmail(messageId, replyData);
        if (response.data) {
          commit("ADD_MESSAGE", response.data);
        }
        return response;
      } catch (error) {
        console.error("Error replying to email:", error);
        throw error;
      }
    },

    async validateEmailAddresses(_, addresses) {
      try {
        return await validateEmailAddresses(addresses);
      } catch (error) {
        console.error("Error validating email addresses:", error);
        throw error;
      }
    },

    async fetchUserEmailAddresses({ commit, getters }) {
      // Only fetch if user is authenticated
      if (!getters.isAuthenticated) {
        console.warn(
          "Cannot fetch user email addresses: user not authenticated"
        );
        return { data: [] };
      }

      try {
        const response = await getUserEmailAddresses();
        // The response.data contains the array directly
        const addresses = response.data || response;
        commit("SET_USER_EMAIL_ADDRESSES", addresses);
        return { data: addresses };
      } catch (error) {
        console.error("Error fetching user email addresses:", error);

        // If it's a 401 error, don't throw - just return empty result
        if (error.response && error.response.status === 401) {
          console.warn(
            "User email addresses fetch failed due to authentication"
          );
          commit("SET_USER_EMAIL_ADDRESSES", []);
          return { data: [] };
        }

        throw error;
      }
    },

    async fetchEmailTemplates({ commit }) {
      try {
        const response = await getEmailTemplates();
        commit("SET_EMAIL_TEMPLATES", response.data.results || response.data);
        return response;
      } catch (error) {
        console.error("Error fetching email templates:", error);
        throw error;
      }
    },

    async fetchEmailSignatures({ commit }) {
      try {
        const response = await getEmailSignatures();
        commit("SET_EMAIL_SIGNATURES", response.data.results || response.data);
        return response;
      } catch (error) {
        console.error("Error fetching email signatures:", error);
        throw error;
      }
    },

    async createEmailSignature({ commit }, signatureData) {
      try {
        const response = await createEmailSignature(signatureData);
        commit("ADD_EMAIL_SIGNATURE", response.data);
        return response;
      } catch (error) {
        console.error("Error creating email signature:", error);
        throw error;
      }
    },

    async updateEmailSignature({ commit }, { signatureId, signatureData }) {
      try {
        const response = await updateEmailSignature(signatureId, signatureData);
        commit("UPDATE_EMAIL_SIGNATURE", response.data);
        return response;
      } catch (error) {
        console.error("Error updating email signature:", error);
        throw error;
      }
    },

    async deleteEmailSignature({ commit }, signatureId) {
      try {
        const response = await deleteEmailSignature(signatureId);
        commit("REMOVE_EMAIL_SIGNATURE", signatureId);
        return response;
      } catch (error) {
        console.error("Error deleting email signature:", error);
        throw error;
      }
    },

    async fetchConversations({ commit }, params = {}) {
      try {
        const response = await getConversations(params);
        commit("SET_CONVERSATIONS", response.data.results || response.data);
        return response;
      } catch (error) {
        console.error("Error fetching conversations:", error);
        throw error;
      }
    },

    async fetchAllConversations({ commit }, params = {}) {
      try {
        const response = await getConversations(params);
        if (params.page === 1) {
          commit("SET_CONVERSATIONS", response.results || response);
        }
        return response;
      } catch (error) {
        console.error("Error fetching all conversations:", error);
        throw error;
      }
    },

    async fetchConversationsForEmail(
      { commit },
      { emailId, folder, page, page_size }
    ) {
      try {
        const response = await getConversations({
          email_address: emailId,
          folder: folder,
          page: page,
          page_size: page_size,
        });
        if (page === 1) {
          commit("SET_CONVERSATIONS", response.results || response);
        }
        return response;
      } catch (error) {
        console.error("Error fetching conversations for email:", error);
        throw error;
      }
    },

    async fetchConversation({ commit }, conversationId) {
      try {
        const response = await getConversation(conversationId);
        commit("SET_CURRENT_CONVERSATION", response.data);
        return response;
      } catch (error) {
        console.error("Error fetching conversation:", error);
        throw error;
      }
    },

    async markConversationAsRead(_, conversationId) {
      try {
        return await markConversationAsRead(conversationId);
      } catch (error) {
        console.error("Error marking conversation as read:", error);
        throw error;
      }
    },

    async deleteConversation(_, conversationId) {
      try {
        return await deleteConversation(conversationId);
      } catch (error) {
        console.error("Error deleting conversation:", error);
        throw error;
      }
    },

    async bulkMessageAction({ commit }, { messageIds, action }) {
      try {
        const response = await bulkMessageAction({ messageIds, action });
        messageIds.forEach((id) => {
          switch (action) {
            case "mark_read":
              commit("UPDATE_MESSAGE", { id, read: true });
              break;
            case "mark_unread":
              commit("UPDATE_MESSAGE", { id, read: false });
              break;
            case "star":
              commit("UPDATE_MESSAGE", { id, is_starred: true });
              break;
            case "unstar":
              commit("UPDATE_MESSAGE", { id, is_starred: false });
              break;
            case "move_to_trash":
              commit("UPDATE_MESSAGE", { id, folder: "trash" });
              break;
            case "move_to_archive":
              commit("UPDATE_MESSAGE", { id, folder: "archive" });
              break;
            case "move_to_spam":
              commit("UPDATE_MESSAGE", { id, folder: "spam" });
              break;
          }
        });
        return response;
      } catch (error) {
        console.error("Error performing bulk action:", error);
        throw error;
      }
    },

    async fetchMessageStats({ commit }) {
      try {
        const response = await getMessageStats();
        commit("SET_EMAIL_STATS", response.data);
        return response;
      } catch (error) {
        console.error("Error fetching message stats:", error);
        throw error;
      }
    },

    async searchConversations(_, params) {
      try {
        return await searchConversations(params);
      } catch (error) {
        console.error("Error searching conversations:", error);
        throw error;
      }
    },
  },
});
